import { Component, OnInit, OnDestroy } from '@angular/core';
import { ManualTimeseriesUploadService } from '@app/api/dmc/api/manualTimeseriesUpload.service';
import { UploadStatus, ImportStatus } from '@app/api/dmc';
import { interval, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { HeaderComponent } from '@shared/components/header/header.component';
import { TranslatePipe } from '@ngx-translate/core';
import { TableComponent } from '@shared/components/table/table.component';
import { FormsModule, ReactiveFormsModule, FormControl, FormGroup, Validators } from '@angular/forms';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { CardComponent } from '@shared/components/card/card.component';
import { TableConfig } from '@shared/interfaces/table/table-config';
import { manualTimeseriesResultTable } from '@shared/table-configs/meteringpoint/manual-estimation-result.table';
import { ButtonDirective } from '@shared/components/input/button/button.directive';
import { ToastService } from '@app/core/services/toast.service';
import { ToggleComponent } from '@shared/components/input/toggle/toggle.component';
import { AlertService } from '@app/core/services/alert.service';

@Component({
  selector: 'app-manual-timeseries-import',
  templateUrl: './manual-timeseries-import.component.html',
  imports: [
    HeaderComponent,
    TableComponent,
    TranslatePipe,
    FormsModule,
    InputGroupComponent,
    LabelDirective,
    ReactiveFormsModule,
    CardComponent,
    ButtonDirective,
    ToggleComponent
  ],
  styleUrls: ['./manual-timeseries-import.component.css']
})
export class ManualTimeseriesImportComponent implements OnInit, OnDestroy {
  selectedFile: File | null = null;
  uploading = false;
  recentImports: UploadStatus[] = [];
  private pollingSubscription: Subscription | null = null;
  ImportStatus = ImportStatus;

  public readonly manualTimeseriesResultTable: TableConfig<UploadStatus> = manualTimeseriesResultTable;

  public form: FormGroup = new FormGroup({
    csvFile: new FormControl<File | null>(null, Validators.required),
    shouldBeLocked: new FormControl<boolean>(false),
  });

  constructor(
    private readonly importService: ManualTimeseriesUploadService,
    private readonly toastService: ToastService,
    private readonly alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.loadRecentImports();
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  onRowSelected(selected: UploadStatus[]): void {
    this.alertService.close('global'); //
    const row = selected[0];
    if (row?.errorMessages?.length) {
      // Option A: Use AlertService to show a list of errors in the global alert area
      this.alertService.dangerGlobal(row.errorMessages, true);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.item(0) ?? null;

    this.form.get('csvFile')?.setValue(file);
    this.form.get('csvFile')?.markAsDirty();
    this.form.get('csvFile')?.updateValueAndValidity();
  }

  uploadFile(): void {
    if (this.form.invalid) return;

    const file = this.form.get('csvFile')?.value as File | null;
    if (!file) return;

    const wantsLocked = this.form.get('shouldBeLocked')?.value ?? false;

    this.uploading = true;
    this.importService.manualTimeseriesUploadPost(file, wantsLocked).subscribe({
      next: () => {
        this.uploading = false;
        this.loadRecentImports();
        this.toastService.show('meteringpoint.csvupload.uploadsuccess', '', 'success');
      },
      error: (error) => {
        this.uploading = false;
        this.toastService.show('meteringpoint.csvupload.uploadfailed', '' ,'error');
      }
    });
  }

  loadRecentImports(): void {
    this.importService.manualTimeseriesUploadGet().subscribe({
      next: (imports) => {
        this.recentImports = imports;
      },
      error: (error) => {
        console.error('Error loading recent imports', error);
      }
    });
  }

  private startPolling(): void {
    this.pollingSubscription = interval(60000)
      .pipe(switchMap(() => this.importService.manualTimeseriesUploadGet()))
      .subscribe({
        next: (imports) => {
          this.recentImports = imports;
        }
      });
  }

  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }
}
